<template>
  <transition name="acct-fade">
    <div v-if="store.account_modal_open" class="acct-overlay" @click.self="close">
      <div class="acct-modal">
        <button class="acct-close" aria-label="Close" @click="close">×</button>

        <!-- Identity -->
        <div class="acct-head">
          <div class="acct-avatar">
            <img v-if="store.querent_avatar_url" :src="store.querent_avatar_url" :alt="store.querent_name" />
            <span v-else class="acct-avatar-ph">{{ initial }}</span>
          </div>
          <div class="acct-id">
            <h2 class="acct-name">{{ store.querent_name || 'Your Account' }}</h2>
            <button class="acct-mirror-link" @click="goMirror">Edit name &amp; avatar in the Mirror →</button>
          </div>
        </div>

        <!-- How to reach you (texting, 2026-10-31): the email and the phone,
             each confirmed or not. A phone is added only by a texted code. -->
        <section class="acct-section acct-reach" data-testid="acct-reach">
          <span class="acct-section-title"><i class="fas fa-address-card"></i> How to reach you</span>
          <div class="acct-reach-head">
            <label class="acct-label" for="acct-email">Email</label>
            <template v-if="reach.email">
              <span v-if="reach.emailVerified === true" class="acct-status ok" data-testid="acct-email-status">✓ Confirmed</span>
              <span v-else-if="reach.emailVerified === false" class="acct-status" data-testid="acct-email-status">Not confirmed</span>
            </template>
          </div>
          <input id="acct-email" v-model="email" type="email" class="acct-input" autocomplete="email" />
          <div class="acct-row-actions">
            <span v-if="emailMsg" class="acct-note" :class="{ ok: emailOk }">{{ emailMsg }}</span>
            <button class="acct-btn" data-testid="acct-email-save" :disabled="savingEmail || !email.trim()" @click="saveEmail">
              {{ savingEmail ? 'Saving…' : 'Save email' }}
            </button>
          </div>

          <div class="acct-reach-head acct-phone-head">
            <span class="acct-label">Phone</span>
            <span v-if="reach.phone && reach.phoneVerified" class="acct-status ok" data-testid="acct-phone-status">✓ Confirmed</span>
            <span v-else-if="reach.phone" class="acct-status" data-testid="acct-phone-status">Not confirmed</span>
          </div>
          <!-- The number on file (it stays in view while a new one is confirmed). -->
          <div v-if="reach.phone" class="acct-phone-row">
            <span class="acct-phone-num" data-testid="acct-phone-number">{{ shownPhone(reach.phone) }}</span>
            <template v-if="phoneStep === 'idle' && !removeAsk">
              <button class="acct-link" data-testid="acct-phone-change" @click="startPhone">Change</button>
              <button class="acct-link danger" data-testid="acct-phone-remove" @click="removeAsk = true">Remove</button>
            </template>
          </div>
          <div v-if="removeAsk" class="acct-phone-ask" data-testid="acct-phone-remove-ask">
            <p>Remove this number? Texts to it stop.</p>
            <div class="acct-row-actions">
              <button class="acct-link ghost" data-testid="acct-phone-remove-no" @click="removeAsk = false">Keep it</button>
              <button class="acct-btn danger" data-testid="acct-phone-remove-yes" :disabled="phoneBusy" @click="dropPhone">
                {{ phoneBusy ? 'Removing…' : 'Remove' }}
              </button>
            </div>
          </div>
          <button v-if="!reach.phone && phoneStep === 'idle'" class="acct-add" data-testid="acct-phone-add"
                  :disabled="!reachLoaded || !reach.smsEnabled" @click="startPhone">+ Add phone</button>
          <!-- A number, then its code. -->
          <div v-if="phoneStep === 'enter'" class="acct-phone-step">
            <input v-model="phoneInput" type="tel" class="acct-input" autocomplete="tel" inputmode="tel"
                   placeholder="(808) 555-0134" aria-label="Your mobile number" data-testid="acct-phone-input"
                   @keydown.enter.prevent="sendPhoneCode" />
            <p class="acct-phone-help">We'll text you a code to confirm it.</p>
            <div class="acct-row-actions">
              <button class="acct-link ghost" data-testid="acct-phone-cancel" @click="cancelPhone">Cancel</button>
              <button class="acct-btn" data-testid="acct-phone-send" :disabled="phoneBusy || !phoneInput.trim()" @click="sendPhoneCode">
                {{ phoneBusy ? 'Sending…' : 'Send code' }}
              </button>
            </div>
          </div>
          <div v-else-if="phoneStep === 'code'" class="acct-phone-step">
            <p class="acct-phone-help" data-testid="acct-phone-said">We texted a code to {{ shownPhone(phonePending) }}.</p>
            <input v-model="phoneCode" type="text" class="acct-input acct-code" inputmode="numeric" autocomplete="one-time-code"
                   maxlength="6" placeholder="000000" aria-label="Six-digit code" data-testid="acct-phone-code"
                   @keydown.enter.prevent="confirmPhoneCode" />
            <div class="acct-row-actions">
              <button class="acct-link ghost" :disabled="phoneBusy" data-testid="acct-phone-resend" @click="sendPhoneCode">Send it again</button>
              <button class="acct-link ghost" data-testid="acct-phone-cancel" @click="cancelPhone">Cancel</button>
              <button class="acct-btn" data-testid="acct-phone-confirm" :disabled="phoneBusy || phoneCode.trim().length < 6" @click="confirmPhoneCode">
                {{ phoneBusy ? 'Checking…' : 'Confirm' }}
              </button>
            </div>
          </div>
          <p v-if="phoneLine" class="acct-phone-msg" :class="{ ok: phoneMsgOk }" role="status" data-testid="acct-phone-msg">{{ phoneLine }}</p>
        </section>

        <!-- Notifications (texting, 2026-10-31): per event, an Email tick and a
             Text tick. Email IS the address's place on the opt-out list (the
             unsubscribe link sets the same one). Text waits for a confirmed
             phone and is never on unless they tick it. -->
        <section class="acct-section acct-notify" data-testid="acct-notifications">
          <div class="acct-notify-head">
            <span class="acct-section-title"><i class="fas fa-bell"></i> Notifications</span>
          </div>
          <div v-for="ev in reach.events" :key="ev.event" class="acct-event" :data-testid="`acct-event-${ev.event}`">
            <div class="acct-notify-row">
              <div class="acct-notify-words">
                <p class="acct-notify-title">{{ ev.label }}</p>
                <p v-if="ev.event === 'arrival'" class="acct-notify-sub">{{ ARRIVAL_SUB }}</p>
              </div>
              <div class="acct-ticks">
                <label class="acct-tick" :class="{ off: ev.locked || !reachLoaded }">
                  <input type="checkbox" :checked="ev.email" :disabled="notifyBusy || ev.locked || !reachLoaded"
                         data-testid="acct-tick-email" @change="tick(ev, 'email', $event)" />
                  <span class="acct-tick-box" aria-hidden="true"></span><span class="acct-tick-name">Email</span>
                </label>
                <label class="acct-tick" :class="{ off: !canText }">
                  <input type="checkbox" :checked="ev.text" :disabled="notifyBusy || !canText"
                         data-testid="acct-tick-text" @change="tick(ev, 'text', $event)" />
                  <span class="acct-tick-box" aria-hidden="true"></span><span class="acct-tick-name">Text</span>
                </label>
              </div>
            </div>
            <p v-if="!canText" class="acct-text-need" data-testid="acct-text-need">{{ TEXT_NEEDS_PHONE }}</p>
            <p v-else-if="ev.text" class="acct-text-disclosure" data-testid="acct-text-disclosure">{{ TEXT_DISCLOSURE }}</p>
          </div>
          <p v-if="notifyNote" class="acct-notify-note" role="status" data-testid="acct-notify-note">{{ notifyNote }}</p>
          <p v-if="notifyErr" class="acct-note err acct-notify-err" role="alert" data-testid="acct-notify-err">{{ notifyErr }}</p>
          <p class="acct-notify-foot">The count on Messages always shows. Sign-in codes always come.</p>
        </section>

        <!-- Password / security -->
        <section class="acct-section">
          <div class="acct-section-head">
            <span class="acct-section-title"><i class="fas fa-lock"></i> Password</span>
            <button v-if="!pwOpen" class="acct-link" @click="pwOpen = true">Change</button>
          </div>
          <div v-if="pwOpen" class="acct-pw">
            <input v-model="pw.old" type="password" class="acct-input" placeholder="Current password" autocomplete="current-password" />
            <input v-model="pw.new1" type="password" class="acct-input" placeholder="New password" autocomplete="new-password" />
            <input v-model="pw.new2" type="password" class="acct-input" placeholder="Confirm new password" autocomplete="new-password" />
            <div class="acct-row-actions">
              <span v-if="pwMsg" class="acct-note" :class="{ ok: pwOk }">{{ pwMsg }}</span>
              <button class="acct-link ghost" @click="closePw">Cancel</button>
              <button class="acct-btn" :disabled="savingPw || !pwValid" @click="savePassword">
                {{ savingPw ? 'Updating…' : 'Update password' }}
              </button>
            </div>
          </div>
        </section>

        <!-- Danger zone -->
        <section class="acct-section acct-danger">
          <div class="acct-section-head">
            <span class="acct-section-title danger"><i class="fas fa-triangle-exclamation"></i> Delete account</span>
            <button v-if="!delOpen" class="acct-link danger" @click="delOpen = true">Delete…</button>
          </div>
          <div v-if="delOpen" class="acct-del">
            <p class="acct-del-warn">This permanently deletes your account, readings, journal, and altar. This cannot be undone.</p>
            <label class="acct-label">Type <b>DELETE</b> to confirm</label>
            <input v-model="delConfirm" type="text" class="acct-input" placeholder="DELETE" autocomplete="off" />
            <div class="acct-row-actions">
              <span v-if="delMsg" class="acct-note err">{{ delMsg }}</span>
              <button class="acct-link ghost" @click="delOpen = false; delConfirm = ''">Cancel</button>
              <button class="acct-btn danger" :disabled="deleting || delConfirm !== 'DELETE'" @click="confirmDelete">
                {{ deleting ? 'Deleting…' : 'Permanently delete' }}
              </button>
            </div>
          </div>
        </section>

        <div class="acct-version">v{{ appVersion }}</div>
      </div>
    </div>
  </transition>
</template>

<script setup>
/**
 * AccountModal — opened from the top-right avatar/name. How to reach you (the
 * email, the phone by a texted code), Notifications (per event, Email and Text
 * ticks), password change, and account deletion. Toggled globally via
 * store.account_modal_open.
 */
import { ref, reactive, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { store } from '@/store'
import { saveQuerentAccount, changePassword, deleteQuerentAccount, getNotifications, setNotification,
  addPhone, confirmPhone, removePhone } from '@/api-service'
import { notificationNote } from '@/utils/account-notifications'
import { readReach, shownPhone, reachMessage, ARRIVAL_SUB, TEXT_NEEDS_PHONE, TEXT_DISCLOSURE,
  TEXT_UNTICKED_BY_CHANGE } from '@/utils/account-reach'
import { contactKind, phoneDigits } from '@/utils/gift-send'

const router = useRouter()
const appVersion = '1.2.4'

const email = ref('')
const savingEmail = ref(false)
const emailMsg = ref('')
const emailOk = ref(false)

const pwOpen = ref(false)
const pw = reactive({ old: '', new1: '', new2: '' })
const savingPw = ref(false)
const pwMsg = ref('')
const pwOk = ref(false)

const delOpen = ref(false)
const delConfirm = ref('')
const deleting = ref(false)
const delMsg = ref('')

// How to reach you and Notifications: one shape from the server (account-reach).
const reach = ref(readReach(null))
const reachLoaded = ref(false)
const notifyBusy = ref(false)
const notifyErr = ref('')
const canText = computed(() => reachLoaded.value && reach.value.phoneVerified)
// The Email tick's line (opted out from an email, a bounce), as before.
const notifyNote = computed(() => {
  const ev = reach.value.events.find((e) => e.event === 'arrival') || reach.value.events[0]
  return ev ? notificationNote({ on: ev.email, ...ev }, reach.value.email || store.querent_email) : ''
})
function applyReach (payload) {
  if (payload && (payload.events || 'on' in payload || 'phone' in payload)) reach.value = readReach(payload)
}
async function loadNotify() {
  try { reach.value = readReach(await getNotifications(store.session_id)); reachLoaded.value = true } catch (e) { reachLoaded.value = false }
  // The page may not hold the address yet (opened before get-querent answered): the server's.
  if (!email.value && reach.value.email) email.value = reach.value.email
}
async function tick(ev, channel, domEvent) {
  const want = !!(domEvent && domEvent.target ? domEvent.target.checked : !ev[channel])
  // The box always shows what the server holds (a refusal puts it back).
  const putBack = () => {
    const now = reach.value.events.find((e) => e.event === ev.event) || ev
    if (domEvent && domEvent.target) domEvent.target.checked = !!now[channel]
  }
  if (notifyBusy.value || (channel === 'text' && !canText.value) || (channel === 'email' && ev.locked)) { putBack(); return }
  notifyBusy.value = true; notifyErr.value = ''
  try { applyReach(await setNotification(store.session_id, ev.event, channel, want)) }
  catch (e) { notifyErr.value = reachMessage(e) }
  finally { notifyBusy.value = false; putBack() }
}

// The phone: 'idle' | 'enter' (the number) | 'code' (the texted code).
const phoneStep = ref('idle')
const phoneInput = ref('')
const phonePending = ref('')
const phoneCode = ref('')
const phoneBusy = ref(false)
const phoneMsg = ref('')
const phoneMsgOk = ref(false)
const removeAsk = ref(false)
const SMS_OFF = reachMessage({ reason: 'sms_unavailable', ok: false })
// Texting not on yet: said beside the waiting "+ Add phone".
const phoneLine = computed(() => phoneMsg.value
  || (reachLoaded.value && !reach.value.phone && !reach.value.smsEnabled && phoneStep.value === 'idle' ? SMS_OFF : ''))
function startPhone() {
  phoneStep.value = 'enter'; phoneInput.value = ''; phoneCode.value = ''; phoneMsg.value = ''; removeAsk.value = false
}
function cancelPhone() { phoneStep.value = 'idle'; phoneInput.value = ''; phoneCode.value = ''; phoneMsg.value = '' }
async function sendPhoneCode() {
  if (phoneBusy.value) return
  phoneMsg.value = ''; phoneMsgOk.value = false
  const typed = phoneStep.value === 'code' ? phonePending.value : phoneInput.value.trim()
  if (contactKind(typed) !== 'phone') { phoneMsg.value = 'Add the whole number, with the area code.'; return }
  const number = phoneDigits(typed)
  phoneBusy.value = true
  try {
    const r = await addPhone(store.session_id, number)
    if (r && r.ok === false) { phoneMsg.value = reachMessage(r); return }
    phonePending.value = number; phoneCode.value = ''; phoneStep.value = 'code'
  } catch (e) { phoneMsg.value = reachMessage(e) }
  finally { phoneBusy.value = false }
}
async function confirmPhoneCode() {
  if (phoneBusy.value || phoneCode.value.trim().length < 6) return
  phoneBusy.value = true; phoneMsg.value = ''
  try {
    // A different number than before: the server unticks Text (the yes was
    // for the old number). Say so, so it is not a surprise.
    const before = reach.value
    const hadText = !!(before.phone && before.phone !== phonePending.value && before.events.some((e) => e.text))
    const r = await confirmPhone(store.session_id, phoneCode.value.trim())
    if (r && r.ok === false) { phoneMsg.value = reachMessage(r, "That code didn't work. Check it, or send a new one."); return }
    if (r && (r.events || 'phone' in r)) applyReach(r)
    else await loadNotify()
    phoneStep.value = 'idle'; phoneInput.value = ''; phoneCode.value = ''
    if (hadText && !reach.value.events.some((e) => e.text)) { phoneMsg.value = TEXT_UNTICKED_BY_CHANGE; phoneMsgOk.value = true }
  } catch (e) { phoneMsg.value = reachMessage(e, "That code didn't work. Check it, or send a new one.") }
  finally { phoneBusy.value = false }
}
async function dropPhone() {
  if (phoneBusy.value) return
  phoneBusy.value = true; phoneMsg.value = ''
  try {
    const r = await removePhone(store.session_id)
    if (r && (r.events || 'phone' in r)) applyReach(r)
    else await loadNotify()
    removeAsk.value = false
  } catch (e) { phoneMsg.value = reachMessage(e) }
  finally { phoneBusy.value = false }
}

const initial = computed(() => (store.querent_name || '✦').trim().charAt(0).toUpperCase())
const pwValid = computed(() => pw.old && pw.new1.length >= 8 && pw.new1 === pw.new2)

// Seed/clear fields each time the modal opens.
watch(() => store.account_modal_open, (open) => {
  if (open) {
    email.value = realEmail(store.querent_email)
    emailMsg.value = ''; pwMsg.value = ''; delMsg.value = ''
    pwOpen.value = false; delOpen.value = false; delConfirm.value = ''
    phoneStep.value = 'idle'; phoneMsg.value = ''; removeAsk.value = false; notifyErr.value = ''
    pw.old = ''; pw.new1 = ''; pw.new2 = ''
    loadNotify()
  }
})

// A phone-made account holds a placeholder address (ending .internal) that is never
// mailed: the field shows nothing, so they can add a real one.
function realEmail(address) {
  const v = (address || '').trim()
  return v.endsWith('.internal') ? '' : v
}
function close() { store.account_modal_open = false }
function closePw() { pwOpen.value = false; pw.old = ''; pw.new1 = ''; pw.new2 = ''; pwMsg.value = '' }
function goMirror() {
  close()
  router.push({ path: '/', query: { panel: 'altar', tab: 'mirror' } }).catch(() => {})
}

async function saveEmail() {
  if (savingEmail.value || !email.value.trim()) return
  savingEmail.value = true; emailMsg.value = ''
  try {
    await saveQuerentAccount({
      querent_id: store.querent_id,
      name: store.querent_name || '',
      email: email.value.trim(),
      session_id: store.session_id,
    })
    store.querent_email = email.value.trim()
    emailOk.value = true; emailMsg.value = 'Saved'
    // A new address carries an opt-out, and leaves a bounce behind.
    loadNotify()
  } catch (e) {
    emailOk.value = false; emailMsg.value = 'Could not save email'
  } finally { savingEmail.value = false }
}

function parsePwError(e) {
  const d = e?.response?.data
  if (d) {
    if (d.old_password) return 'Current password is incorrect.'
    if (d.new_password2 || d.new_password1) return [].concat(d.new_password2 || d.new_password1).join(' ')
    if (d.detail) return d.detail
  }
  return 'Could not update password.'
}

async function savePassword() {
  if (savingPw.value || !pwValid.value) return
  savingPw.value = true; pwMsg.value = ''
  try {
    await changePassword(pw.new1, pw.new2, pw.old)
    pwOk.value = true; pwMsg.value = 'Password updated'
    pw.old = ''; pw.new1 = ''; pw.new2 = ''
    setTimeout(() => { pwOpen.value = false; pwMsg.value = '' }, 1200)
  } catch (e) {
    pwOk.value = false; pwMsg.value = parsePwError(e)
  } finally { savingPw.value = false }
}

async function confirmDelete() {
  if (deleting.value || delConfirm.value !== 'DELETE') return
  deleting.value = true; delMsg.value = ''
  try {
    await deleteQuerentAccount(store.session_id)
    // Account is gone — clear everything and return to a clean entry.
    localStorage.clear()
    window.location.replace('/')
  } catch (e) {
    delMsg.value = 'Could not delete the account. Please try again.'
    deleting.value = false
  }
}
</script>

<style scoped>
.acct-overlay { position: fixed; inset: 0; z-index: 1400; background: rgba(0,0,0,0.78);
  display: flex; align-items: flex-start; justify-content: center; padding: 7vh 16px 16px; overflow-y: auto; }
.acct-modal { position: relative; width: 100%; max-width: 440px; box-sizing: border-box;
  background: #14071f; border: 1px solid rgba(240,217,163,0.2); border-radius: 18px;
  padding: 22px 22px 16px; box-shadow: 0 26px 60px -16px rgba(0,0,0,0.85); color: #e9dcff; }
.acct-close { position: absolute; top: 12px; right: 14px; background: none; border: none;
  color: #9b86c9; font-size: 24px; line-height: 1; cursor: pointer; }
.acct-close:hover { color: #f0d9a3; }

.acct-head { display: flex; align-items: center; gap: 14px; margin-bottom: 22px; }
.acct-avatar { width: 60px; height: 60px; border-radius: 14px; overflow: hidden; flex: none;
  border: 1px solid rgba(240,217,163,0.3); background: #1a0c2e; }
.acct-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.acct-avatar-ph { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;
  font-family: 'Cinzel', serif; font-size: 24px; color: #C9A94E; }
.acct-name { font-family: 'Cinzel', serif; font-size: 19px; color: #f5e8c0; margin: 0 0 3px; }
.acct-mirror-link { background: none; border: none; padding: 0; cursor: pointer;
  color: rgba(195,169,255,0.75); font-size: 12px; font-family: inherit; }
.acct-mirror-link:hover { color: #C3A9FF; }

.acct-section { padding: 14px 0; border-top: 1px solid rgba(255,255,255,0.08); }
.acct-section-head { display: flex; align-items: center; justify-content: space-between; }
.acct-section-title { font-family: 'Cinzel', serif; font-size: 13px; letter-spacing: 0.04em; color: #f0d9a3; }
.acct-section-title.danger { color: #e89bb0; }
.acct-section-title i { margin-right: 7px; opacity: 0.8; }
.acct-label { display: block; font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase;
  color: rgba(255,255,255,0.4); margin-bottom: 7px; }
.acct-input { width: 100%; box-sizing: border-box; background: rgba(255,255,255,0.04);
  border: 1px solid rgba(195,169,255,0.22); border-radius: 10px; color: #E8D8FF; font-size: 14px;
  padding: 10px 12px; outline: none; margin-bottom: 8px; font-family: inherit; }
.acct-input:focus { border-color: rgba(102,51,153,0.6); background: rgba(102,51,153,0.16); }
.acct-input::placeholder { color: rgba(195,169,255,0.4); }

.acct-row-actions { display: flex; align-items: center; justify-content: flex-end; gap: 12px; margin-top: 4px; }
.acct-note { margin-right: auto; font-size: 12px; color: #e89bb0; }
.acct-note.ok { color: #8fd6a8; }
.acct-note.err { color: #f0a3a3; }
.acct-btn { font-family: 'Cinzel', serif; font-size: 12.5px; letter-spacing: 0.03em; color: #1a0f2e;
  background: linear-gradient(180deg, #FFDFA3, #cdae6a); border: none; border-radius: 9px;
  padding: 9px 18px; cursor: pointer; }
.acct-btn:disabled { opacity: 0.5; cursor: default; }
.acct-btn.danger { color: #fff; background: linear-gradient(180deg, #c0506a, #8a2f45); }
.acct-link { background: none; border: none; cursor: pointer; font-family: inherit; font-size: 12.5px;
  color: rgba(195,169,255,0.8); padding: 4px 2px; }
.acct-link:hover { color: #C3A9FF; }
.acct-link.ghost { color: rgba(195,169,255,0.55); }
.acct-link.danger { color: #e89bb0; }
.acct-link.danger:hover { color: #f5b8c6; }

.acct-pw, .acct-del { margin-top: 12px; }

/* Notifications */
.acct-notify-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-top: 14px; }
.acct-notify-words { min-width: 0; }
.acct-notify-title { margin: 0 0 3px; font-size: 14px; font-weight: 600; color: #f3eaff; }
.acct-notify-sub { margin: 0; font-size: 12.5px; line-height: 1.45; color: rgba(232,216,255,0.62); }
.acct-switch { flex: none; position: relative; width: 52px; height: 30px; margin-top: 2px; border-radius: 999px; border: 0; cursor: pointer;
  background: rgba(195,169,255,0.18); transition: background-color 180ms ease; padding: 0; }
.acct-switch.on { background: linear-gradient(180deg, #FFDFA3, #cdae6a); }
.acct-switch-knob { position: absolute; top: 4px; left: 4px; width: 22px; height: 22px; border-radius: 50%; background: #9b86c9;
  transition: transform 180ms ease, background-color 180ms ease; }
.acct-switch.on .acct-switch-knob { transform: translateX(22px); background: #14071f; }
.acct-switch:disabled { cursor: default; opacity: 0.55; }
.acct-switch:focus-visible, .acct-btn:focus-visible { outline: 2px solid #C3A9FF; outline-offset: 3px; }
.acct-notify-note { margin: 12px 0 0; padding: 10px 12px; border-radius: 10px; border: 1px solid rgba(240,217,163,0.25);
  background: rgba(255,255,255,0.04); font-size: 12.5px; line-height: 1.5; color: #f0d9a3; }
/* How to reach you */
.acct-reach .acct-section-title { display: block; margin-bottom: 12px; }
.acct-reach-head { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.acct-phone-head { margin-top: 14px; }
.acct-status { font-size: 12px; color: rgba(232,216,255,0.62); }
.acct-status.ok { color: #8fd6a8; }
.acct-phone-row { display: flex; align-items: center; gap: 14px; }
.acct-phone-num { margin-right: auto; font-size: 15px; color: #f3eaff; letter-spacing: 0.02em; }
.acct-add { background: none; border: 1px dashed rgba(240,217,163,0.45); border-radius: 10px; color: #f0d9a3;
  font-family: inherit; font-size: 13.5px; padding: 10px 14px; width: 100%; text-align: left; cursor: pointer; }
.acct-add:hover:not(:disabled) { border-color: rgba(240,217,163,0.8); }
.acct-add:disabled { opacity: 0.5; cursor: default; }
.acct-phone-step { margin-top: 10px; }
.acct-phone-help { margin: -2px 0 8px; font-size: 12.5px; line-height: 1.45; color: rgba(232,216,255,0.7); }
.acct-code { text-align: center; letter-spacing: 8px; font-size: 20px; }
.acct-phone-ask { margin-top: 10px; padding: 10px 12px; border-radius: 10px; border: 1px solid rgba(232,155,176,0.35); }
.acct-phone-ask p { margin: 0 0 4px; font-size: 13px; color: #f3eaff; }
.acct-phone-msg { margin: 8px 0 0; font-size: 12.5px; line-height: 1.45; color: #f0d9a3; }
.acct-phone-msg.ok { color: #8fd6a8; }

/* Notifications: per event, Email and Text ticks */
.acct-event + .acct-event { border-top: 1px dashed rgba(255,255,255,0.08); }
.acct-ticks { flex: none; display: flex; gap: 8px; }
.acct-tick { position: relative; display: inline-flex; align-items: center; gap: 7px; min-height: 40px; padding: 0 12px 0 10px;
  border-radius: 999px; border: 1px solid rgba(195,169,255,0.28); cursor: pointer; font-size: 13px; color: #f3eaff; }
.acct-tick input { position: absolute; opacity: 0; width: 1px; height: 1px; margin: 0; }
.acct-tick-box { width: 18px; height: 18px; border-radius: 5px; border: 1.5px solid rgba(240,217,163,0.7); box-sizing: border-box;
  display: inline-flex; align-items: center; justify-content: center; }
.acct-tick input:checked + .acct-tick-box { background: linear-gradient(180deg, #FFDFA3, #cdae6a); border-color: #cdae6a; }
.acct-tick input:checked + .acct-tick-box::after { content: ''; width: 5px; height: 9px; margin-top: -2px;
  border: solid #14071f; border-width: 0 2px 2px 0; transform: rotate(45deg); }
.acct-tick input:focus-visible + .acct-tick-box { outline: 2px solid #C3A9FF; outline-offset: 3px; }
.acct-tick.off { opacity: 0.42; cursor: default; }
.acct-text-need, .acct-text-disclosure { margin: 6px 0 0; text-align: right; font-size: 11.5px; line-height: 1.45; color: rgba(232,216,255,0.6); }
.acct-text-disclosure { color: rgba(232,216,255,0.75); }
.acct-notify-err { margin: 10px 0 0; }
@media (max-width: 420px) {
  .acct-notify-row { flex-direction: column; gap: 10px; }
  .acct-ticks { align-self: flex-end; }
}
.acct-notify-foot { margin: 14px 0 0; font-size: 11.5px; line-height: 1.45; color: rgba(195,169,255,0.6); }
@media (prefers-reduced-motion: reduce) { .acct-switch, .acct-switch-knob { transition: none; } }
.acct-del-warn { font-size: 12.5px; color: rgba(232,216,255,0.7); line-height: 1.5; margin: 0 0 12px; }
.acct-danger .acct-del-warn b { color: #f5b8c6; }
.acct-version { text-align: center; font-size: 11px; color: rgba(195,169,255,0.35); margin-top: 14px; }

.acct-fade-enter-active, .acct-fade-leave-active { transition: opacity 0.2s ease; }
.acct-fade-enter-from, .acct-fade-leave-to { opacity: 0; }
.acct-two { display: flex; gap: 8px; }
.acct-two .acct-input { flex: 1; min-width: 0; }
.acct-hint { display: block; font-weight: 400; font-size: 11px; opacity: 0.7; margin-top: 2px; }
</style>
